import { Permission } from '@up2stay/up2promo-api-client';
import { Button, Card, CardContent, Skeleton } from '@up2stay/up2promo-ui';
import {
  CalendarDays,
  Download,
  Loader2,
  Lock,
  Plus,
  ScanLine,
  Search,
} from 'lucide-react';
import { Navigate, useSearchParams } from 'react-router-dom';

import { AppIonPage } from '@/core/components/app-ion-page';
import { AppPageContent } from '@/core/components/app-page-content';
import { AppPageLayout } from '@/core/components/app-page-layout';
import { BusinessCardScanFlow } from '@/core/components/leads/business-card-scan-flow';
import { LeadsBoardTab } from '@/core/components/leads/leads-board-tab';
import { LeadsListTab } from '@/core/components/leads/leads-list-tab';
import { LeadsScopeTabs } from '@/core/components/leads/leads-scope-tabs';
import { LeadsViewToggle } from '@/core/components/leads/leads-view-toggle';
import { useLeadsPage } from '@/core/components/leads/use-leads-page';
import { ShellLink } from '@/core/components/navigation/shell-link';
import { PageHeader } from '@/core/components/page-header';
import { GUIDANCE_TARGET } from '@/core/guidance/constants';
import { useIsMobile } from '@/core/hooks/use-mobile';
import { usePermissions } from '@/core/hooks/use-permissions';
import { shellPaths } from '@/core/router/app-paths';

export function LeadsPage() {
  const [searchParams] = useSearchParams();
  const page = useLeadsPage();
  const isMobile = useIsMobile();
  const { can } = usePermissions();
  const {
    dict,
    profileLoading,
    currentWorkspace,
    activeTab,
    setActiveTab,
    listScope,
    setListScope,
    scopeCounts,
    scopeLabels,
    canSeeOthersPrivate,
    canExportLeads,
    isExporting,
    filteredLeads,
    handleExportCsv,
    setBusinessCardScanOpen,
    requestCreateLead,
    requestSearch,
    workspaceLeadsAlwaysShared,
    eventsEnabled,
  } = page;
  // Phones don't show the sidebar's Leads → Events entry; link it here.
  const showMobileEventsLink =
    isMobile && eventsEnabled && can(Permission.EVENTS_READ);

  // Compat: legacy `?tab=events` → dedicated Events hub route.
  if (searchParams.get('tab') === 'events') {
    return <Navigate to={shellPaths.leadsEvents} replace />;
  }

  const listDict = dict.leads.list as {
    newLead?: string;
    scope?: {
      othersPrivateShort?: string;
      policyShared?: string;
      policySettings?: string;
    };
  };
  const scanLabel =
    (dict.leads.businessCardScan as { button?: string })?.button ??
    'Visitenkarte scannen';
  const addLabel =
    (dict.leads.create as { addCard?: string })?.addCard ??
    listDict.newLead ??
    'Lead hinzufügen';

  const viewToggle = (
    <LeadsViewToggle
      value={activeTab === 'boards' ? 'boards' : 'list'}
      onChange={(mode) => setActiveTab(mode === 'boards' ? 'boards' : 'list')}
      labels={{
        list: dict.leads.tabs?.list ?? 'Liste',
        board: (dict.leads.tabs as { board?: string })?.board ?? 'Board',
      }}
    />
  );

  const headerActions = isMobile ? (
    <div className="flex items-center gap-2">
      <Button
        type="button"
        variant="outline"
        size="icon"
        className="size-11 rounded-full border-[color:var(--surface-border)] bg-card"
        aria-label={dict.leads.list.searchButton}
        onClick={() => {
          // Search lives in the list view; open it there from the board.
          setActiveTab('list');
          requestSearch();
        }}
      >
        <Search className="size-[18px]" />
      </Button>
      <Button
        type="button"
        variant="outline"
        size="icon"
        className="size-11 rounded-full border-[color:var(--surface-border)] bg-card"
        aria-label={scanLabel}
        onClick={() => setBusinessCardScanOpen(true)}
      >
        <ScanLine className="size-[18px]" />
      </Button>
      <Button
        type="button"
        variant="primary"
        size="icon"
        className="size-11 rounded-full shadow-[var(--shadow-brand-cta)]"
        aria-label={addLabel}
        onClick={() => requestCreateLead()}
      >
        <Plus className="size-[18px]" />
      </Button>
    </div>
  ) : (
    <div className="flex flex-wrap items-center gap-2">
      <Button
        type="button"
        variant="outline"
        className="h-9 gap-1.5 rounded-[var(--radius-input)] border-[color:var(--surface-border)] bg-card px-3.5 text-[13px] font-semibold"
        onClick={() => setBusinessCardScanOpen(true)}
      >
        <ScanLine className="size-[15px]" aria-hidden />
        {scanLabel}
      </Button>
      <Button
        type="button"
        variant="primary"
        className="h-9 gap-1.5 rounded-[var(--radius-input)] px-3.5 text-[13px] font-semibold shadow-[var(--shadow-brand-cta)]"
        onClick={() => requestCreateLead()}
      >
        <Plus className="size-[15px]" aria-hidden />
        {addLabel}
      </Button>
    </div>
  );

  return (
    <AppIonPage>
      <AppPageContent onRefresh={page.refreshLeads}>
        <AppPageLayout className="min-w-0 overflow-x-hidden">
          <div data-guidance-target="leads-inbox">
            <PageHeader
              title={dict.leads.title}
              breadcrumb={
                isMobile ? undefined : (
                  <>
                    {dict.nav.navDomainWork}
                    <span className="mx-1.5 text-muted-foreground/70">/</span>
                    {dict.nav.leads}
                  </>
                )
              }
              actions={headerActions}
              actionsLayout="inline"
            />
          </div>

          {profileLoading ? (
            <Card>
              <CardContent className="flex flex-col gap-4 p-5 sm:p-6">
                <Skeleton className="h-10 w-full" />
                <Skeleton className="h-64 w-full" />
              </CardContent>
            </Card>
          ) : (
            <div className="flex min-w-0 flex-col gap-3 md:gap-4">
              <div
                className="flex min-w-0 flex-col gap-2"
                data-guidance-target={GUIDANCE_TARGET.leadsToolbar}
              >
                <div className="flex min-w-0 flex-wrap items-center justify-between gap-3">
                  <div className="flex min-w-0 flex-wrap items-center gap-3">
                    <LeadsScopeTabs
                      value={listScope}
                      onChange={setListScope}
                      counts={scopeCounts}
                      canSeeOthersPrivate={canSeeOthersPrivate}
                      fullWidth={isMobile}
                      othersPrivateShortLabel={
                        listDict.scope?.othersPrivateShort
                      }
                      labels={{
                        mine: scopeLabels.mine,
                        workspace: scopeLabels.workspace,
                        othersPrivate: scopeLabels.othersPrivate,
                      }}
                    />
                    {workspaceLeadsAlwaysShared ? (
                      <p className="inline-flex items-center gap-1.5 text-[12.5px] text-muted-foreground">
                        <Lock className="size-[13px] shrink-0" aria-hidden />
                        <span>
                          {listDict.scope?.policyShared ??
                            'Richtlinie: Alle Leads werden geteilt'}
                          {' · '}
                          <ShellLink
                            to={shellPaths.moreWorkspaceSettings}
                            className="font-semibold text-[color:var(--ghost-accent-fg)] underline-offset-2 hover:underline"
                          >
                            {listDict.scope?.policySettings ?? 'Einstellungen'}
                          </ShellLink>
                        </span>
                      </p>
                    ) : null}
                  </div>
                  {!isMobile ? (
                    <div className="flex items-center gap-2">
                      {viewToggle}
                      {canExportLeads ? (
                        <Button
                          type="button"
                          variant="outline"
                          size="icon"
                          className="size-9 shrink-0 rounded-[var(--radius-input)] border-[color:var(--surface-border)] bg-card"
                          disabled={filteredLeads.length === 0 || isExporting}
                          title={dict.leads.list.export}
                          aria-label={dict.leads.list.export}
                          onClick={handleExportCsv}
                        >
                          {isExporting ? (
                            <Loader2 className="size-[15px] animate-spin" />
                          ) : (
                            <Download className="size-[15px]" />
                          )}
                        </Button>
                      ) : null}
                    </div>
                  ) : null}
                </div>
                {isMobile ? (
                  <div className="flex min-w-0 items-center justify-between gap-3">
                    {viewToggle}
                    {showMobileEventsLink ? (
                      <Button
                        asChild
                        variant="outline"
                        className="h-9 gap-1.5 rounded-[var(--radius-input)] border-[color:var(--surface-border)] bg-card px-3 text-[12.5px] font-semibold"
                      >
                        <ShellLink to={shellPaths.leadsEvents}>
                          <CalendarDays className="size-[15px]" aria-hidden />
                          {dict.leads.events.title}
                        </ShellLink>
                      </Button>
                    ) : null}
                  </div>
                ) : null}
              </div>

              {activeTab === 'list' ? (
                <LeadsListTab page={page} showScopeRow={false} />
              ) : null}
              {activeTab === 'boards' ? (
                <LeadsBoardTab page={page} showScopeRow={false} />
              ) : null}
            </div>
          )}
        </AppPageLayout>
        <BusinessCardScanFlow
          open={page.businessCardScanOpen}
          onOpenChange={page.setBusinessCardScanOpen}
          existingLeads={page.leads}
          profileId={page.profile?.id}
          workspaceId={currentWorkspace?.id ?? null}
          onLeadCreated={page.handleLeadCreated}
        />
      </AppPageContent>
    </AppIonPage>
  );
}
